import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Sparkles, ArrowLeft } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

function timeAgo(iso) {
    if (!iso) return "";
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

export default function ChatList() {
    const navigate = useNavigate();
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/matches");
            setMatches(data.matches || []);
        } catch (e) {
            toast.error("Couldn't load chats");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="mx-auto max-w-xl px-5 pt-8" data-testid="chat-list">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate("/app/match")}
                    data-testid="chats-back-btn"
                    className="h-10 w-10 rounded-full hover:bg-accent grid place-items-center lift"
                >
                    <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
                </button>
                <div>
                    <div className="font-display text-3xl font-black tracking-tighter">
                        Your <span className="font-fancy text-gradient-purple">chats</span>
                    </div>
                    <div className="text-xs text-muted-foreground">Mutual matches only</div>
                </div>
            </div>

            <div className="mt-6 space-y-2">
                {loading && matches.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-10">Loading…</div>
                ) : matches.length === 0 ? (
                    <div className="mt-8 rounded-3xl border border-border bg-card p-8 text-center" data-testid="chats-empty">
                        <div className="mx-auto h-14 w-14 rounded-2xl bg-accent grid place-items-center text-accent-foreground">
                            <MessageCircle className="h-7 w-7" strokeWidth={2.5} />
                        </div>
                        <div className="mt-4 font-display text-xl font-bold">No matches yet</div>
                        <p className="text-sm text-muted-foreground mt-1">Start swiping — mutual likes appear here.</p>
                        <button
                            onClick={() => navigate("/app/match")}
                            data-testid="chats-start-swiping-btn"
                            className="mt-5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 font-semibold text-sm lift"
                        >
                            Start swiping
                        </button>
                    </div>
                ) : (
                    matches.map((m, i) => {
                        const u = m.user;
                        const avatar = u.picture || (u.photo_urls?.[0] ? toAbsolute(u.photo_urls[0]) : null);
                        return (
                            <motion.button
                                key={m.match_id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.02 * i }}
                                onClick={() => navigate(`/app/chats/${m.match_id}`)}
                                data-testid={`chat-row-${m.match_id}`}
                                className="w-full text-left flex items-center gap-3 rounded-3xl border border-border bg-card p-3 hover:bg-accent/40 lift"
                            >
                                <div className="relative">
                                    <div className="h-14 w-14 rounded-full overflow-hidden bg-accent grid place-items-center text-accent-foreground font-black">
                                        {avatar ? (
                                            <img src={avatar} alt={u.name} className="h-full w-full object-cover" />
                                        ) : (
                                            (u.name || "?").charAt(0).toUpperCase()
                                        )}
                                    </div>
                                    {m.unread_count > 0 && (
                                        <span
                                            data-testid={`chat-unread-${m.match_id}`}
                                            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-black grid place-items-center"
                                        >
                                            {m.unread_count}
                                        </span>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 justify-between">
                                        <div className="font-display font-bold truncate">{u.name}</div>
                                        <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                                            {timeAgo(m.last_message?.created_at || m.created_at)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 text-xs">
                                        <Sparkles className="h-3 w-3 text-primary" strokeWidth={3} />
                                        <span className="text-primary font-semibold">{u.match_percent}%</span>
                                        <span className="text-muted-foreground mx-1">·</span>
                                        <span className={`truncate ${m.unread_count > 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                                            {m.last_message?.body || "Say hi 👋"}
                                        </span>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })
                )}
            </div>
        </div>
    );
}
